import { useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { Card, CardContent } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Badge } from '@/components/ui/badge';
import { 
  HelpCircle, 
  ChevronDown, 
  ChevronRight, 
  Search,
  Wrench,
  Truck,
  Settings,
  DollarSign,
  Palette
} from 'lucide-react';

interface FAQItem {
  id: number;
  question: string;
  answer: string;
  category: 'servis' | 'dodavky' | 'technické' | 'obchod' | 'barvy';
  keywords: string[];
}

interface FAQSectionProps {
  className?: string;
}

// Hardcoded fallback data (not used when API is available)
const fallbackFAQData: FAQItem[] = [
  {
    id: 1,
    question: "Může kamnář inzerovat kamna BH na svém e-shopu včetně ceny?",
    answer: "Ano, pokud splňuje podmínky spolupráce a má vystavené kamna. Téma cen se aktuálně řeší podle změny strategie - více informací najdete v interním obchodním manuálu.",
    category: "obchod",
    keywords: ["inzerovat", "e-shop", "cena", "podmínky", "strategie"]
  },
  {
    id: 2,
    question: "Jaký servis poskytuje BH ke kamnům?",
    answer: "Primárně by servis měl poskytovat kamnář, který musí být na servis řádně proškolen. Připravujeme seznam úkonů, které kamnář musí umět, včetně instruktážních videí. Náš servis bude připraven pomoci v případě potřeby. Samozřejmostí je telefonická online podpora - připravujeme se na možné dotazy a potíže při provozu, ale nejvíce zkušeností získáme během první topné sezóny.",
    category: "servis",
    keywords: ["servis", "školení", "podpora", "kamnář", "videa", "telefonická"]
  },
  {
    id: 3,
    question: "Jaká je dodací lhůta na náhradní díly ke kamnům?",
    answer: "Aktuálně zpracováváme seznam náhradních dílů pro katalog. Tyto díly budou skladem s odesláním do 48 hodin.",
    category: "dodavky",
    keywords: ["dodací lhůta", "náhradní díly", "sklad", "48 hodin", "odesílání"]
  },
  {
    id: 4,
    question: "Dokáže BH garantovat dodržování stejných cen v rámci ČR?",
    answer: "Ano, stejně jako u kotlů garantujeme dodržování jednotných cen v rámci České republiky.",
    category: "obchod",
    keywords: ["ceny", "garance", "ČR", "kotle", "stejné ceny"]
  },
  {
    id: 5,
    question: "Jak probíhá výměna roštu v případě poškození?",
    answer: "Vaničky roštu mají při správném používání životnost 15+ let. Pokud mechanismus půjde ztuha, odšroubuje se plech s nápisem Blaze a mechanismus se promaže vysokoteplotní vazelínou.\n\nPro demontáž roštu je nutné vyjmout všechny tvarovky a krytky mechanismu. Vše je opravitelné zevnitř kamen, takže není nutné kamna odpojovat od kouřovodu nebo je přesouvat.",
    category: "technické",
    keywords: ["rošt", "výměna", "vaničky", "mechanismus", "demontáž", "oprava"]
  },
  {
    id: 6,
    question: "Kdy začne BH dodávat barevní kouřovody?",
    answer: "Tento požadavek aktuálně řešíme. Předpokládáme, že barevné kouřovody by mohly být dostupné v listopadu/prosinci. Termín závisí na podmínkách dodavatele kouřovodů, dodacích lhůtách a lakování. Konečné rozhodnutí bude záviset také na požadavcích na objednávky velkých sérií.",
    category: "barvy",
    keywords: ["kouřovody", "barevné", "listopad", "prosinec", "lakování", "dodavatel"]
  },
  {
    id: 7,
    question: "Kdy bude BH dodávat ke kamnům barvy ve spreji?",
    answer: "Spreje máme připravené a jsou skladem. Budou pravděpodobně součástí ceníku náhradních dílů - přesné informace včetně ceny budou upřesněny.",
    category: "barvy",
    keywords: ["barvy", "sprej", "ceník", "náhradní díly", "skladem"]
  },
  {
    id: 8,
    question: "Jaká bude výdrž páčky mechanické detekce paliva u kamen Spirit?",
    answer: "Přesné informace o výdrži páčky mechanické detekce paliva budou upřesněny podle zkušeností z provozu.",
    category: "technické",
    keywords: ["páčka", "mechanická detekce", "palivo", "Spirit", "výdrž"]
  },
  {
    id: 9,
    question: "Budou kamna splňovat novou normu ČSN 16510 platnou od 1. listopadu 2025?",
    answer: "Kamna Element i Spirit jsou již certifikována podle normy ČSN EN 16510, takže splňují požadavky nové normy.",
    category: "technické",
    keywords: ["norma", "ČSN 16510", "listopad 2025", "Element", "Spirit", "certifikace"]
  },
  {
    id: 10,
    question: "Jaké jsou minimální vzdálenosti kamen od zdí a ostatních hořlavých materiálů?",
    answer: "Minimální vzdálenosti jsou stanoveny podle normy ČSN 06 1008/1997. Přesné údaje najdete v návodu k použití kamen.",
    category: "technické",
    keywords: ["vzdálenosti", "zdi", "hořlavé materiály", "návod", "ČSN 06 1008"]
  },
  {
    id: 11,
    question: "Jaká je skladba izolace zadní a boční stěny kamen?",
    answer: "Boční izolace má tloušťku 10 mm, zadní izolace 20 mm. Používáme materiál Sibrex 140. Izolace se nachází hned za žárobetonovou tvarovkou. Mezi tělesem kamen a plechovými kryty není žádná izolace.",
    category: "technické",
    keywords: ["izolace", "Sibrex 140", "10mm", "20mm", "žárobeton", "tvarovka"]
  },
  {
    id: 12,
    question: "Jak často bude třeba vyměňovat šňůru těsnění?",
    answer: "Frekvence výměny nelze jednoznačně stanovit, závisí na intenzitě používání, kvalitě paliva a způsobu topení. V návodu aktuálně uvádíme minimálně jednou za dva roky, ale plánujeme přeformulování na: \"Těsnící šňůru pravidelně kontrolujte a v případě zjištění netěsnosti ji vyměňte.\" Stejný přístup používají i ostatní výrobci.",
    category: "servis",
    keywords: ["těsnění", "šňůra", "výměna", "dva roky", "kontrola", "netěsnost"]
  },
  {
    id: 13,
    question: "Může si kamna koupit koncový zákazník na e-shopu?",
    answer: "Ano, ale pouze v případě, že se jedná o kamnáře s kamenným obchodem. Platí stejné podmínky jako u první otázky ohledně inzerování.",
    category: "obchod",
    keywords: ["e-shop", "koncový zákazník", "kamnář", "kamenný obchod"]
  },
  {
    id: 14,
    question: "Nehrozí při větším nákladu polen, že při jejich sesuvu prasne sklo?",
    answer: "Ano, toto riziko vždy existuje. Není povoleno do kamen nakládat větší dávku polen, než určuje návod. Při nedodržení této zásady hrozí sesuv polen a prasknutí skla, stejně jako přetápění kamen a poškození jejich součástí. Na tyto závady se nevztahuje záruka.",
    category: "technické",
    keywords: ["polena", "sesuv", "sklo", "prasknutí", "přetápění", "záruka"]
  },
  {
    id: 15,
    question: "Kdy budou k dispozici kamna Spirit s akumulační nástavbou?",
    answer: "Cílem je uvedení na trh během topné sezóny 2026. Věříme, že to může být i dříve. Plánujeme představit verzi s akumulací i s výměníkem na veletrhu ve Veroně, pokud se to podaří realizovat. Více informací najdete v interním obchodním manuálu.",
    category: "technické",
    keywords: ["Spirit", "akumulační nástavba", "2026", "topná sezóna", "Verona", "výměník"]
  },
  {
    id: 16,
    question: "Je možné dostat ještě vyšší slevu, když kamna vystavím na prodejně?",
    answer: "Vyšší sleva je možná pouze na vzorek určený k vystavení na prodejně.",
    category: "obchod",
    keywords: ["sleva", "vystavení", "prodejna", "vzorek"]
  }
];

const categoryConfig = {
  servis: {
    label: 'Servis a údržba',
    icon: Wrench,
    color: 'bg-blue-500/10 text-blue-700 border-blue-200'
  },
  dodavky: {
    label: 'Dodávky a logistika',
    icon: Truck,
    color: 'bg-green-500/10 text-green-700 border-green-200'
  },
  technické: {
    label: 'Technické specifikace',
    icon: Settings,
    color: 'bg-purple-500/10 text-purple-700 border-purple-200'
  },
  obchod: {
    label: 'Obchodní podmínky',
    icon: DollarSign,
    color: 'bg-orange-500/10 text-orange-700 border-orange-200'
  },
  barvy: {
    label: 'Barvy a design',
    icon: Palette,
    color: 'bg-pink-500/10 text-pink-700 border-pink-200'
  }
};

export function FAQSection({ className }: FAQSectionProps) {
  const [searchTerm, setSearchTerm] = useState('');
  const [expandedItems, setExpandedItems] = useState<Set<number>>(new Set());
  const [selectedCategory, setSelectedCategory] = useState<string>('');

  // Fetch FAQ data from API
  const { data: faqData = [], isLoading, error } = useQuery({
    queryKey: ['faq-items', 'cs'],
    queryFn: async () => {
      const response = await fetch('/api/faq?language=cs');
      if (!response.ok) throw new Error('Failed to fetch FAQ items');
      const result = await response.json();
      return result.data as FAQItem[];
    },
    staleTime: 5 * 60 * 1000, // 5 minut
    gcTime: 10 * 60 * 1000 // 10 minut
  });

  const toggleExpanded = (id: number) => {
    const newExpanded = new Set(expandedItems);
    if (newExpanded.has(id)) {
      newExpanded.delete(id);
    } else {
      newExpanded.add(id);
    }
    setExpandedItems(newExpanded);
  };

  const filteredFAQ = faqData.filter((item: FAQItem) => {
    const matchesSearch = !searchTerm || 
      item.question.toLowerCase().includes(searchTerm.toLowerCase()) ||
      item.answer.toLowerCase().includes(searchTerm.toLowerCase()) ||
      item.keywords.some((keyword: string) => keyword.toLowerCase().includes(searchTerm.toLowerCase()));
    
    const matchesCategory = !selectedCategory || item.category === selectedCategory;
    
    return matchesSearch && matchesCategory;
  });

  const categories = Object.keys(categoryConfig) as Array<keyof typeof categoryConfig>;

  // Loading state
  if (isLoading) {
    return (
      <div className={className}>
        <div className="flex items-center gap-2 mb-4">
          <HelpCircle className="h-4 w-4 text-primary" />
          <h4 className="font-medium text-sm">Často kladené otázky (FAQ)</h4>
        </div>
        <div className="text-center py-6">
          <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary mx-auto mb-2"></div>
          <p className="text-sm text-muted-foreground">Načítám FAQ...</p>
        </div>
      </div>
    );
  }

  // Error state
  if (error) {
    return (
      <div className={className}>
        <div className="flex items-center gap-2 mb-4">
          <HelpCircle className="h-4 w-4 text-primary" />
          <h4 className="font-medium text-sm">Často kladené otázky (FAQ)</h4>
        </div>
        <div className="text-center py-6 text-muted-foreground">
          <HelpCircle className="h-8 w-8 mx-auto mb-2 opacity-50" />
          <p className="text-sm">Nepodařilo se načíst FAQ</p>
        </div>
      </div>
    );
  }

  return (
    <div className={className}>
      <div className="flex items-center gap-2 mb-4">
        <HelpCircle className="h-4 w-4 text-primary" />
        <h4 className="font-medium text-sm">Často kladené otázky (FAQ)</h4>
      </div>

      {/* Vyhledávání a filtry */}
      <div className="space-y-3 mb-4">
        <div className="relative">
          <Search className="h-4 w-4 absolute left-3 top-1/2 transform -translate-y-1/2 text-muted-foreground" />
          <Input
            placeholder="Vyhledat v FAQ..."
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            className="pl-9 h-9"
          />
        </div>

        {/* Kategorie filtry */}
        <div className="flex flex-wrap gap-2">
          <Button
            variant={selectedCategory === '' ? 'default' : 'outline'}
            size="sm"
            onClick={() => setSelectedCategory('')}
            className="text-xs h-7"
          >
            Vše ({faqData.length})
          </Button>
          {categories.map(category => {
            const config = categoryConfig[category];
            const count = faqData.filter((item: FAQItem) => item.category === category).length;
            const Icon = config.icon;
            
            return (
              <Button
                key={category}
                variant={selectedCategory === category ? 'default' : 'outline'}
                size="sm"
                onClick={() => setSelectedCategory(category)}
                className="text-xs h-7"
              >
                <Icon className="h-3 w-3 mr-1" />
                {config.label} ({count})
              </Button>
            );
          })}
        </div>
      </div>

      {/* FAQ položky */}
      <div className="space-y-2">
        {filteredFAQ.length === 0 ? (
          <div className="text-center py-6 text-muted-foreground">
            <HelpCircle className="h-8 w-8 mx-auto mb-2 opacity-50" />
            <p className="text-sm">
              {searchTerm || selectedCategory 
                ? 'Nenašly se žádné otázky odpovídající vašim kritériím'
                : 'Žádné FAQ nejsou k dispozici'
              }
            </p>
          </div>
        ) : (
          filteredFAQ.map((item) => {
            const isExpanded = expandedItems.has(item.id);
            const categoryConfig_ = categoryConfig[item.category];
            const CategoryIcon = categoryConfig_.icon;
            
            return (
              <Card key={item.id} className="border-l-4 border-l-primary/20">
                <CardContent className="p-0">
                  <Button
                    variant="ghost"
                    className="w-full justify-start h-auto p-4 text-left"
                    onClick={() => toggleExpanded(item.id)}
                  >
                    <div className="flex items-start gap-3 w-full">
                      <div className="flex-shrink-0 mt-0.5">
                        {isExpanded ? (
                          <ChevronDown className="h-4 w-4" />
                        ) : (
                          <ChevronRight className="h-4 w-4" />
                        )}
                      </div>
                      <div className="flex-1 min-w-0">
                        <div className="font-medium text-sm mb-2 break-words">
                          {item.question}
                        </div>
                        <div className="flex items-center gap-2">
                          <Badge 
                            variant="secondary" 
                            className={`text-xs ${categoryConfig_.color}`}
                          >
                            <CategoryIcon className="h-3 w-3 mr-1" />
                            {categoryConfig_.label}
                          </Badge>
                        </div>
                      </div>
                    </div>
                  </Button>
                  
                  {isExpanded && (
                    <div className="px-4 pb-4 pl-11">
                      <div className="bg-muted/30 rounded-lg p-3 text-sm">
                        <div className="whitespace-pre-line break-words">
                          {item.answer}
                        </div>
                      </div>
                    </div>
                  )}
                </CardContent>
              </Card>
            );
          })
        )}
      </div>

      {filteredFAQ.length > 0 && (
        <div className="mt-4 pt-3 border-t text-xs text-muted-foreground text-center">
          Zobrazeno {filteredFAQ.length} z {faqData.length} otázek
        </div>
      )}
    </div>
  );
}